import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Input } from '@fluentui/react-components';
import { CheckmarkSquare20Filled, Square20Regular, Edit16Regular, Delete16Regular } from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../apiServices/axios';
import { updateWorkspaceDefaults } from '../../redux/usersSlice';
import wsStyles from '../WorkspaceSettings/css/index.module.css';
import styles from './WorkspaceSetupWizard.module.css';

const REQUIRED_FEATURES = ['project_overview', 'dataroom', 'information_requests', 'project_management'];
const RECOMMENDED_FEATURES = ['journal'];
const DEFAULT_PROJECT_TYPES = ['M&A Acquisition', 'Divestiture', 'Technology Due Diligence', 'Post-Merger Integration', 'Strategic Investment', 'Joint Venture'];
const DEFAULT_PROJECT_STATUSES = [
    { name: 'Pipeline', color: '#F8047E' },
    { name: 'Pre-Deal', color: '#FF0D0D' },
    { name: 'Due Diligence', color: '#FF8E15' },
    { name: 'On Hold', color: '#FAB733' },
    { name: 'Closed - Won', color: '#ACB334' },
    { name: 'Closed - Lost', color: '#C6C8D2' },
];
const FEATURE_CATEGORIES = {
    strategic_assessment: {
        name: 'Strategic Assessment & Monitoring',
        features: {
            project_overview: {
                name: 'Project Overview',
                description: 'Every project starts with an overview dashboard summarising deal progress, key metrics, and team activity.',
            },
            maturity_index: {
                name: 'Maturity Index',
                description: 'Assess the technology maturity of a target company using a structured scoring framework across key capability areas.',
            },
            security_index: {
                name: 'Security Index',
                description: "Evaluate the target company's security posture across infrastructure, policies, and practices.",
            },
            team_analyser: {
                name: 'Team Analyser',
                description: "Review and document the organisational structure, leadership capability, and cultural fit of the target company's team.",
            },
            security_governance: {
                name: 'Security Governance',
                description: 'Assess the governance frameworks, compliance posture, and security policies in place at the target company.',
            },
        },
    },
    due_diligence: {
        name: 'Due Diligence & Knowledge',
        features: {
            dataroom: {
                name: 'Dataroom',
                description: 'A secure, multi-workstream virtual data room for managing and sharing deal documents with granular access controls.',
            },
            information_requests: {
                name: 'Information Requests',
                description: 'Manage information and document requests across workstreams and parties. Track responses, follow-ups, and outstanding items in one place.',
            },
            dataroom_wizard: {
                name: 'Dataroom Wizard',
                description: 'A guided tool to help structure and prepare a data room.',
            },
            observations: {
                name: 'Observations',
                description: 'Capture, categorise, and manage consultant and team findings during due diligence. Linked to workstreams and deal stages.',
            },
            lessons_learnt: {
                name: 'Lessons Learnt',
                description: 'Record and access institutional knowledge from completed deals. Build a compounding knowledge base across your portfolio.',
            },
            journal: {
                name: 'Journal',
                description: 'A shared note-taking space for deal teams. Capture informal insights, meeting notes, and commentary throughout the deal lifecycle.',
            },
        },
    },
    project_risk_management: {
        name: 'Project & Risk Management',
        features: {
            project_management: {
                name: 'Project Management',
                description: 'Full project and task management with Kanban boards, task assignment, priorities, due dates, and progress tracking.',
            },
            raid_log: {
                name: 'RAID Log',
                description: 'Track Risks, Assumptions, Issues, and Dependencies across the deal. Flag, assign, and monitor resolution in real time.',
            },
        },
    },
    technical_service: {
        name: 'Technical & Service Delivery',
        features: {
            software_inventory: {
                name: 'Software Inventory',
                description: "Document and assess the target company's software stack, licensing arrangements, and technology dependencies.",
            },
        },
    },
};

const WorkspaceSetupWizard = () => {
    const { workspaceId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const membership = useSelector(state => (Array.isArray(state?.user?.memberships) ? state.user.memberships.find(m => String(m?.workspace?.id) === String(workspaceId)) : null));

    const [workspaceName, setWorkspaceName] = useState(() => membership?.workspace?.name || '');
    const [logoFile, setLogoFile] = useState(null);
    const [logoObjectUrl, setLogoObjectUrl] = useState(null);
    const [serverLogoUrl, setServerLogoUrl] = useState(null);
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [projectStatuses, setProjectStatuses] = useState([]);
    const [newProjectType, setNewProjectType] = useState('');
    const [newProjectStatus, setNewProjectStatus] = useState('');
    const [newProjectStatusColor, setNewProjectStatusColor] = useState('#5CC8FF');
    const [editingTypeIndex, setEditingTypeIndex] = useState(null);
    const [editingTypeName, setEditingTypeName] = useState('');
    const [editingStatusIndex, setEditingStatusIndex] = useState(null);
    const [editingStatusName, setEditingStatusName] = useState('');
    const [editingStatusColor, setEditingStatusColor] = useState('#5CC8FF');
    const [draggedStatusIndex, setDraggedStatusIndex] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteUserType, setInviteUserType] = useState('');
    const [inviteSubmitting, setInviteSubmitting] = useState(false);
    const [workspaceMembers, setWorkspaceMembers] = useState([]);
    const [workspaceInvitations, setWorkspaceInvitations] = useState([]);
    const [revokingInvitationId, setRevokingInvitationId] = useState(null);
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setError('');
            try {
                const { data } = await axiosInstance.get(`accounts/workspaces/${workspaceId}/admin/settings/`);
                if (cancelled) return;
                if (typeof data?.name === 'string') {
                    setWorkspaceName(data.name);
                }
                setServerLogoUrl(data.workspace_logo || null);
                const defaults = Array.isArray(data?.default_lens_features) ? data.default_lens_features : [];
                setSelectedFeatures(Array.from(new Set([...REQUIRED_FEATURES, ...defaults])));
                const typeOptionsRaw = Array.isArray(data?.project_type_options) ? data.project_type_options : [];
                const statusOptionsRaw = Array.isArray(data?.project_status_options) ? data.project_status_options : [];
                const normalizedTypes = typeOptionsRaw.map(item => (typeof item?.name === 'string' ? item.name.trim() : '')).filter(Boolean);
                const normalizedStatuses = statusOptionsRaw
                    .map(item => ({
                        name: typeof item?.name === 'string' ? item.name.trim() : '',
                        color: typeof item?.color === 'string' && item.color ? item.color : '#5CC8FF',
                    }))
                    .filter(item => item.name);
                setProjectTypes(normalizedTypes.length ? normalizedTypes : DEFAULT_PROJECT_TYPES);
                setProjectStatuses(normalizedStatuses.length ? normalizedStatuses : DEFAULT_PROJECT_STATUSES);
            } catch (e) {
                if (!cancelled) {
                    setError('Could not load workspace. Please refresh and try again.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [workspaceId]);

    const refreshWorkspaceMembers = useCallback(async () => {
        if (!workspaceId) return;
        try {
            const { data } = await axiosInstance.get(`accounts/workspaces/${workspaceId}/admin/members/`);
            const members = Array.isArray(data?.members) ? data.members : [];
            const invites = Array.isArray(data?.invitations) ? data.invitations : [];
            setWorkspaceMembers(members);
            setWorkspaceInvitations(invites);
        } catch {
            // Silent failure – Step 4 can still be used without the list.
        }
    }, [workspaceId]);

    useEffect(() => {
        refreshWorkspaceMembers();
    }, [refreshWorkspaceMembers]);

    useEffect(() => {
        return () => {
            if (logoObjectUrl) {
                URL.revokeObjectURL(logoObjectUrl);
            }
        };
    }, [logoObjectUrl]);

    const displayLogoUrl = logoObjectUrl || serverLogoUrl;

    const hasLogo = Boolean(logoFile || serverLogoUrl);
    const nameOk = (workspaceName || '').trim().length > 0;
    const canContinueStep1 = nameOk && hasLogo && !loading;
    const workspaceNameLabel = (workspaceName || membership?.workspace?.name || 'Workspace').trim() || 'Workspace';

    const allFeatureKeys = useMemo(() => Object.values(FEATURE_CATEGORIES).flatMap(category => Object.keys(category.features)), []);

    const openFilePicker = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const onFileChange = e => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        const okType = /^image\/(png|jpeg|jpg)$/i.test(f.type);
        if (!okType) {
            setError('Please use a PNG or JPG image.');
            return;
        }
        setError('');
        setLogoFile(f);
        setLogoObjectUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(f);
        });
    };

    const toggleFeature = featureKey => {
        if (REQUIRED_FEATURES.includes(featureKey)) return;
        setSelectedFeatures(prev => {
            const has = prev.includes(featureKey);
            const next = has ? prev.filter(k => k !== featureKey) : [...prev, featureKey];
            return Array.from(new Set([...REQUIRED_FEATURES, ...next]));
        });
    };

    const handleAddAllFeatures = () => {
        setSelectedFeatures(Array.from(new Set([...REQUIRED_FEATURES, ...allFeatureKeys])));
    };

    const handleRemoveAllOptionalFeatures = () => {
        setSelectedFeatures([...REQUIRED_FEATURES]);
    };

    const saveStep1 = async () => {
        if (!canContinueStep1 || submitting) return;
        setError('');
        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('name', workspaceName.trim());
            if (logoFile) {
                formData.append('workspace_logo', logoFile);
            }
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: {
                        name: data.name,
                        workspace_logo: data.workspace_logo,
                    },
                }),
            );
        } catch (e) {
            const msg = e?.response?.data?.name?.[0] || e?.response?.data?.workspace_logo?.[0] || e?.response?.data?.detail || 'Could not save. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
            throw e;
        } finally {
            setSubmitting(false);
        }
    };

    const saveStep2 = async () => {
        if (submitting) return;
        setError('');
        setSubmitting(true);
        try {
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, {
                default_lens_features: selectedFeatures,
            });
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: {
                        default_lens_features: data.default_lens_features,
                    },
                }),
            );
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Could not save. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
            throw e;
        } finally {
            setSubmitting(false);
        }
    };

    const saveStep3 = async () => {
        if (submitting) return;
        setError('');
        setSubmitting(true);
        try {
            const payload = {
                project_type_options: projectTypes.map(name => ({ name, color: '#8E2DE2' })),
                project_status_options: projectStatuses.map((item, idx) => ({ name: item.name, color: item.color, order: idx })),
            };
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, payload);
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: {
                        project_type_options: data.project_type_options,
                        project_status_options: data.project_status_options,
                    },
                }),
            );
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Could not save. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
            throw e;
        } finally {
            setSubmitting(false);
        }
    };

    const goNext = async () => {
        if (step === 1 && !canContinueStep1) return;
        setError('');
        try {
            if (step === 1) {
                await saveStep1();
            } else if (step === 2) {
                await saveStep2();
            } else if (step === 3) {
                await saveStep3();
            }
            setStep(prev => Math.min(prev + 1, 4));
        } catch {
            // error already surfaced via setError
        }
    };

    const goBack = () => {
        setError('');
        setStep(prev => Math.max(prev - 1, 1));
    };

    const addProjectType = () => {
        const value = (newProjectType || '').trim();
        if (!value) return;
        if (projectTypes.some(item => item.toLowerCase() === value.toLowerCase())) return;
        setProjectTypes(prev => [...prev, value]);
        setNewProjectType('');
    };

    const removeProjectType = index => {
        setProjectTypes(prev => prev.filter((_, i) => i !== index));
    };

    const startEditProjectType = index => {
        setEditingTypeIndex(index);
        setEditingTypeName(projectTypes[index] || '');
    };

    const saveEditProjectType = () => {
        const value = (editingTypeName || '').trim();
        if (editingTypeIndex == null || !value) {
            setEditingTypeIndex(null);
            return;
        }
        setProjectTypes(prev => prev.map((item, i) => (i === editingTypeIndex ? value : item)));
        setEditingTypeIndex(null);
        setEditingTypeName('');
    };

    const addProjectStatus = () => {
        const value = (newProjectStatus || '').trim();
        if (!value) return;
        if (projectStatuses.some(item => item.name.toLowerCase() === value.toLowerCase())) return;
        setProjectStatuses(prev => [...prev, { name: value, color: newProjectStatusColor || '#5CC8FF' }]);
        setNewProjectStatus('');
    };

    const removeProjectStatus = index => {
        setProjectStatuses(prev => prev.filter((_, i) => i !== index));
    };

    const startEditProjectStatus = index => {
        const status = projectStatuses[index];
        if (!status) return;
        setEditingStatusIndex(index);
        setEditingStatusName(status.name);
        setEditingStatusColor(status.color || '#5CC8FF');
    };

    const saveEditProjectStatus = () => {
        const value = (editingStatusName || '').trim();
        if (editingStatusIndex == null || !value) {
            setEditingStatusIndex(null);
            return;
        }
        setProjectStatuses(prev =>
            prev.map((item, i) =>
                i === editingStatusIndex
                    ? {
                          ...item,
                          name: value,
                          color: editingStatusColor || '#5CC8FF',
                      }
                    : item,
            ),
        );
        setEditingStatusIndex(null);
    };

    const handleStatusDragStart = index => {
        setDraggedStatusIndex(index);
    };

    const handleStatusDragOver = event => {
        event.preventDefault();
    };

    const handleStatusDrop = index => {
        if (draggedStatusIndex === null || draggedStatusIndex === index) return;
        const updated = [...projectStatuses];
        const [moved] = updated.splice(draggedStatusIndex, 1);
        updated.splice(index, 0, moved);
        setDraggedStatusIndex(null);
        setProjectStatuses(updated);
    };

    const isEmail = str => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(str);
    };

    const handleSendInvite = async () => {
        const email = (inviteEmail || '').trim();
        if (!email || !inviteUserType || inviteSubmitting) return;
        if (!isEmail(email)) {
            const msg = 'Please enter a valid email address.';
            setError(msg);
            toast.error(msg, { position: 'top-right' });
            return;
        }
        setError('');
        setInviteSubmitting(true);
        try {
            const role = inviteUserType === 'FULL_ACCESS' ? 'WORKSPACE_MEMBER' : 'WORKSPACE_MEMBER';
            const { data } = await axiosInstance.post(`accounts/workspaces/${workspaceId}/invite/`, {
                email,
                role,
            });
            setInviteEmail('');
            setInviteUserType('');
            await refreshWorkspaceMembers();
            const detail = data?.detail || 'Invite sent';
            toast.success(detail, { position: 'top-right' });
        } catch (e) {
            const msg = e?.response?.data?.email || e?.response?.data?.detail || e?.response?.data?.message || 'Could not send invite. Please try again.';
            const finalMsg = typeof msg === 'string' ? msg : 'Could not send invite. Please try again.';
            setError(finalMsg);
            toast.error(finalMsg, { position: 'top-right' });
        } finally {
            setInviteSubmitting(false);
        }
    };

    const handleFinalSubmit = async () => {
        if (step !== 4 || submitting) return;
        setError('');
        setSubmitting(true);
        try {
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, {
                is_setuped: true,
            });
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: {
                        is_setuped: data.is_setuped,
                    },
                }),
            );
            navigate('/project', { replace: true });
        } catch (e) {
            const msg = e?.response?.data?.name?.[0] || e?.response?.data?.workspace_logo?.[0] || e?.response?.data?.detail || 'Could not save. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderStep1 = () => (
        <div style={{ maxWidth: '400px' }}>
            <p className={styles.step}>Step 1 of 4</p>
            <h1 className={styles.mainTitle}>Set up your workspace</h1>
            <div className={styles.fieldBlock}>
                <div className={styles.labelRow}>
                    <label className={styles.fieldLabel} htmlFor="workspace-setup-name">
                        Workspace Name
                    </label>
                </div>
                <div className={styles.nameInput}>
                    <Input
                        id="workspace-setup-name"
                        value={workspaceName}
                        onChange={(e, data) => setWorkspaceName(data.value)}
                        placeholder="Pre-populated, editable text input"
                        appearance="outline"
                    />
                </div>
            </div>
            <div className={styles.fieldBlock}>
                <div className="d-flex align-items-center justify-content-between">
                    <span className={styles.fieldLabel}>Workspace Logo</span>
                    <button type="button" className={styles.changeLink} onClick={openFilePicker}>
                        Change
                    </button>
                </div>

                <input ref={fileInputRef} className={styles.hiddenInput} type="file" accept="image/png,image/jpeg,image/jpg" aria-hidden tabIndex={-1} onChange={onFileChange} />

                <div
                    className={styles.dropzone}
                    role="button"
                    tabIndex={0}
                    onClick={openFilePicker}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openFilePicker();
                        }
                    }}>
                    {displayLogoUrl ? (
                        <div className={styles.previewWrap}>
                            <img className={styles.previewImg} src={displayLogoUrl} alt="" />
                        </div>
                    ) : null}
                    {!displayLogoUrl && <p className={styles.dropzoneHint}>{'Supported file formats: PNG/JPG\nRecommended size 200×200px\n\nYou can add or change this later'}</p>}
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div style={{ maxWidth: '550px' }}>
            <p className={styles.step}>{`Step 2 of 4  |  ${workspaceNameLabel}`}</p>
            <h1 className={styles.mainTitle}>Choose what your team sees by default</h1>
            <p className={styles.stepDescription}>
                Select the features enabled across all projects in your workspace. Required features are always on. All others can be turned on or off - Project Admins can adjust
                these per project at any time.
            </p>
            <p className={styles.stepDescriptionBold}>Feature selection applies to Full Access users only. VDR users have access to Dataroom and Information Requests only.</p>

            {Object.entries(FEATURE_CATEGORIES).map(([key, category]) => (
                <div key={key} className={styles.featureCategory}>
                    <h3 className={styles.categoryTitle}>{category.name}</h3>
                    {Object.entries(category.features).map(([featureKey, feature]) => {
                        const checked = selectedFeatures.includes(featureKey);
                        const required = REQUIRED_FEATURES.includes(featureKey);
                        const recommended = RECOMMENDED_FEATURES.includes(featureKey);
                        return (
                            <label key={featureKey} className={styles.featureItem}>
                                <span className={styles.featureIconWrap}>
                                    {checked ? (
                                        <CheckmarkSquare20Filled className={required ? styles.requiredIcon : styles.checkedIcon} />
                                    ) : (
                                        <Square20Regular className={styles.uncheckedIcon} />
                                    )}
                                </span>
                                <input type="checkbox" className={styles.featureNativeInput} checked={checked} disabled={required} onChange={() => toggleFeature(featureKey)} />
                                <span className={styles.featureTextWrap}>
                                    <span className={styles.featureTitle}>
                                        {feature.name}
                                        {required ? <span className={styles.featureMeta}> (Required)</span> : null}
                                        {!required && recommended ? <span className={styles.featureMeta}> (Recommended)</span> : null}
                                    </span>
                                    <span className={styles.featureDescription}>{feature.description}</span>
                                </span>
                            </label>
                        );
                    })}
                </div>
            ))}
        </div>
    );

    const renderStep3 = () => (
        <div style={{ maxWidth: '500px' }}>
            <p className={styles.step}>{`Step 3 of 4  |  ${workspaceNameLabel}`}</p>
            <h1 className={styles.mainTitle}>Define your project settings</h1>
            <p className={styles.stepDescription}>
                Review and customise the default project types and lifecycle stages for your workspace. These are pre-configured based on your setup - edit, reorder, or add your
                own.
            </p>

            <div className={styles.step3Section}>
                <h3 className={styles.categoryTitle}>Default Project Types</h3>
                <p className={styles.step3Hint}>Project types help categorise deals across your portfolio. Project Admins select a type when creating a new project.</p>
                {projectTypes.map((type, idx) => (
                    <div key={`${type}-${idx}`} className={styles.simpleRow}>
                        {editingTypeIndex === idx ? (
                            <Input
                                value={editingTypeName}
                                onChange={(e, data) => setEditingTypeName(data.value)}
                                onBlur={saveEditProjectType}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') saveEditProjectType();
                                    if (e.key === 'Escape') setEditingTypeIndex(null);
                                }}
                                appearance="outline"
                            />
                        ) : (
                            <span className={styles.simpleRowName}>{type}</span>
                        )}
                        <div className={styles.simpleActions}>
                            <button type="button" className={styles.iconBtn} onClick={() => startEditProjectType(idx)}>
                                <Edit16Regular />
                            </button>
                            <button type="button" className={styles.iconBtn} onClick={() => removeProjectType(idx)}>
                                <Delete16Regular />
                            </button>
                        </div>
                    </div>
                ))}
                <br />
                <div className={wsStyles.projectTypeInputRow}>
                    <input
                        type="text"
                        className={wsStyles.projectTypeInput}
                        placeholder="Add New Project Type"
                        value={newProjectType}
                        onChange={e => setNewProjectType(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addProjectType();
                            }
                        }}
                    />
                    <button type="button" className={wsStyles.addTypeButton} onClick={addProjectType} disabled={!newProjectType.trim()}>
                        Add
                    </button>
                </div>
            </div>

            <div className={styles.step3Section}>
                <h3 className={styles.categoryTitle}>Default Project Statuses</h3>
                <p className={styles.step3Hint}>
                    These stages define your deal lifecycle and appear as columns on your portfolio Kanban board. Drag to reorder.
                    <span className={styles.step3HintItalic}> Note: The order shown here reflects the left-to-right column order on your portfolio Kanban board.</span>
                </p>
                {projectStatuses.length === 0 ? (
                    <div className={wsStyles.centered}>No default project statuses yet.</div>
                ) : (
                    <ul className={wsStyles.statusList}>
                        {projectStatuses.map((statusItem, idx) => (
                            <li
                                key={`${statusItem.name}-${idx}`}
                                className={draggedStatusIndex === idx ? `${wsStyles.statusRow} ${wsStyles.statusRowDragging}` : wsStyles.statusRow}
                                draggable
                                onDragStart={() => handleStatusDragStart(idx)}
                                onDragOver={handleStatusDragOver}
                                onDrop={() => handleStatusDrop(idx)}>
                                <div className={wsStyles.statusMain}>
                                    {editingStatusIndex !== idx && <span className={wsStyles.statusDragHandle}>⋮⋮</span>}
                                    {editingStatusIndex === idx ? (
                                        <>
                                            <input
                                                type="text"
                                                className={wsStyles.statusEditInput}
                                                value={editingStatusName}
                                                onChange={e => setEditingStatusName(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        saveEditProjectStatus();
                                                    } else if (e.key === 'Escape') {
                                                        e.preventDefault();
                                                        setEditingStatusIndex(null);
                                                    }
                                                }}
                                            />
                                            <div className={wsStyles.statusColorInputWrapper}>
                                                <input
                                                    type="color"
                                                    className={wsStyles.statusColorInput}
                                                    value={editingStatusColor}
                                                    onChange={e => setEditingStatusColor(e.target.value)}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <span className={wsStyles.statusName}>{statusItem.name}</span>
                                    )}
                                </div>
                                <div className={wsStyles.statusActions}>
                                    {editingStatusIndex !== idx && <span className={wsStyles.statusColorDot} style={{ backgroundColor: statusItem.color || '#8E2DE2' }} />}
                                    {editingStatusIndex === idx ? (
                                        <>
                                            <button type="button" className={wsStyles.saveTypeButton} onClick={saveEditProjectStatus}>
                                                Save
                                            </button>
                                            <button type="button" className={wsStyles.deleteTypeButton} onClick={() => setEditingStatusIndex(null)}>
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <Edit16Regular className="ms-3" color="var(--Neutral-Gray-6)" type="button" onClick={() => startEditProjectStatus(idx)} />
                                            <Delete16Regular className="ms-3" color="var(--Neutral-Gray-6)" type="button" onClick={() => removeProjectStatus(idx)} />
                                        </>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
                <div className={wsStyles.statusInputRow}>
                    <input
                        type="text"
                        className={wsStyles.projectTypeInput}
                        placeholder="Add New Project Status"
                        value={newProjectStatus}
                        onChange={e => setNewProjectStatus(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addProjectStatus();
                            }
                        }}
                    />
                    <div className={wsStyles.statusColorInputWrapper}>
                        <input type="color" className={wsStyles.statusColorInput} value={newProjectStatusColor} onChange={e => setNewProjectStatusColor(e.target.value)} />
                    </div>
                    <button type="button" className={wsStyles.addTypeButton} onClick={addProjectStatus} disabled={!newProjectStatus.trim()}>
                        Add
                    </button>
                </div>
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div style={{ maxWidth: '720px' }}>
            <p className={styles.step}>{`Step 4 of 4  |  ${workspaceNameLabel}`}</p>
            <h1 className={styles.mainTitle}>
                Invite your team to Lens <span className={styles.stepMeta}>(Optional)</span>
            </h1>
            <p className={styles.stepDescription}>
                Add your team members to the workspace. Workspace members can also be invited by Project Admins when setting up a project. Each Full Access user is billed as a seat
                from their join date. You can manage users at any time from your workspace settings.
            </p>

            <div className={styles.step3Section}>
                <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Total seats: {workspaceMembers.length + workspaceInvitations.length}</span>
                </div>
                <p className={styles.step3Hint}>Billing starts from each user's join date.</p>
            </div>

            <div className={styles.step4Table}>
                <div className={styles.step4HeaderRow}>
                    <span className={styles.step4HeaderCell}>Email address</span>
                    <span className={styles.step4HeaderCell}>User Type</span>
                    <span className={styles.step4HeaderCell}>Join Date</span>
                    <span className={styles.step4HeaderCell}></span>
                </div>
                {workspaceMembers.map(member => {
                    const created = member.created_at ? new Date(member.created_at) : null;
                    const joinDate = created && !Number.isNaN(created.getTime()) ? created.toLocaleDateString() : '—';
                    return (
                        <div key={`member-${member.id}`} className={styles.step4BodyRow}>
                            <span className={styles.step4Cell}>{member.email}</span>
                            <span className={styles.step4Cell}>
                                {member.role === 'WORKSPACE_SUPER_ADMIN' ? 'Super Admin' : member.role === 'WORKSPACE_ADMIN' ? 'Workspace Admin' : 'Full Access User'}
                            </span>
                            <span className={styles.step4Cell}>{joinDate}</span>
                        </div>
                    );
                })}
                {workspaceInvitations.map(invite => {
                    const created = invite.created_date ? new Date(invite.created_date) : null;
                    const inviteDate = created && !Number.isNaN(created.getTime()) ? created.toLocaleDateString() : '';
                    const statusLabel = invite.is_expired ? 'Invitation expired' : 'Invited';
                    const actionLabel = invite.is_expired ? 'Resend Invitation' : 'Revoke Invitation';
                    const isBusy = revokingInvitationId === invite.id;

                    const handleInviteAction = async () => {
                        if (!invite.id || !workspaceId || isBusy) return;
                        // For now both expired and active use revoke on backend; resend can be added later.
                        setRevokingInvitationId(invite.id);
                        try {
                            await axiosInstance.delete(`accounts/workspaces/${workspaceId}/admin/invitations/${invite.id}/`);
                            await refreshWorkspaceMembers();
                            toast.success(invite.is_expired ? 'Invitation removed' : 'Invitation revoked', { position: 'top-right' });
                        } catch (e) {
                            const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Could not update invitation. Please try again.';
                            toast.error(typeof msg === 'string' ? msg : 'Could not update invitation. Please try again.', {
                                position: 'top-right',
                            });
                        } finally {
                            setRevokingInvitationId(null);
                        }
                    };

                    return (
                        <div key={`invite-${invite.id || invite.email}`} className={styles.step4BodyRow}>
                            <span className={styles.step4Cell}>{invite.email}</span>
                            <span className={styles.step4Cell}>
                                {invite.role === 'WORKSPACE_SUPER_ADMIN' ? 'Super Admin' : invite.role === 'WORKSPACE_ADMIN' ? 'Workspace Admin' : 'Full Access User'}
                            </span>
                            <span className={styles.step4Cell}>{statusLabel} </span>
                            <span className={styles.step4Cell}>
                                {' '}
                                <button type="button" className={styles.stepActionLink} disabled={isBusy} onClick={handleInviteAction}>
                                    {actionLabel}
                                </button>
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className={styles.step4InviteRow}>
                <input className={styles.step4EmailInput} placeholder="Add email address" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} type="email" />
                <select className={styles.step4UserTypeBtn} value={inviteUserType} onChange={e => setInviteUserType(e.target.value)}>
                    <option value="" disabled>
                        Select User Type
                    </option>
                    <option value="FULL_ACCESS">Full Access User</option>
                    <option value="VDR">VDR User</option>
                </select>
                <button type="button" className={styles.step4SendBtn} disabled={!inviteEmail.trim() || !inviteUserType || inviteSubmitting} onClick={handleSendInvite}>
                    {inviteSubmitting ? 'Sending…' : 'Send Invite'}
                </button>
            </div>
        </div>
    );

    return (
        <div className={wsStyles.editBody}>
            <div className="row m-0 p-0 ps-5">
                <div className="col-sm-12 col-md-1 m-0 p-0" />
                <div className="col-sm-12 col-md-6 m-0 p-0">
                    <div className={`${wsStyles.formBodies} `}>
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                        {step === 4 && renderStep4()}

                        {error ? (
                            <p className={styles.error} role="alert">
                                {error}
                            </p>
                        ) : null}

                        <div className={styles.actions}>
                            {step === 2 ? (
                                <div className={styles.step2Actions} style={{ maxWidth: '550px' }}>
                                    <button type="button" className={`${wsStyles.submitButton} ${submitting ? styles.submitDisabled : ''}`} disabled={submitting} onClick={goNext}>
                                        Continue
                                    </button>
                                    <div className={styles.step2Secondary}>
                                        <button type="button" className={styles.stepActionLink} onClick={handleAddAllFeatures}>
                                            Add all
                                        </button>
                                        <button type="button" className={styles.stepActionLink} onClick={handleRemoveAllOptionalFeatures}>
                                            Remove all
                                        </button>
                                    </div>
                                </div>
                            ) : step === 3 ? (
                                <div className={styles.step2Actions} style={{ maxWidth: '500px' }}>
                                    <button type="button" className={`${wsStyles.submitButton} ${submitting ? styles.submitDisabled : ''}`} disabled={submitting} onClick={goNext}>
                                        Continue
                                    </button>
                                    <button type="button" className={styles.stepActionLink} onClick={goBack}>
                                        Back
                                    </button>
                                </div>
                            ) : step === 4 ? (
                                <div className={styles.step2Actions} style={{ maxWidth: '700px' }}>
                                    <button type="button" className={styles.stepActionLink} onClick={goBack}>
                                        Back
                                    </button>
                                    <button type="button" className={styles.stepActionLink} disabled={submitting} onClick={handleFinalSubmit}>
                                        {submitting ? 'Finishing…' : 'Skip for now'}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className={`${wsStyles.submitButton} ${step === 1 && !canContinueStep1 ? styles.submitDisabled : ''}`}
                                    disabled={step === 1 ? !canContinueStep1 : false}
                                    onClick={goNext}>
                                    Continue
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkspaceSetupWizard;
